import { formatRubleAmount } from '@/lib/market-money'
import type {
  YmBestsellerTier,
  YmDriftState,
  YmMinimumForPromosMode,
  YmOfferPricing,
  YmReconcileDirection,
} from '@/lib/api/schemas/ymarket-pricing.schema'

export const DRIFT_LABELS: Record<YmDriftState, string> = {
  in_sync: 'Совпадает',
  price: 'Цена расходится',
  discount_base: 'Зачёркнутая расходится',
  minimum_for_promos: 'Минимум расходится',
  missing_on_ym: 'Нет на Маркете',
  never_collected: 'Не собрано',
  forge_price_not_set: 'Наша цена не задана',
}

export const DRIFT_TONES: Record<YmDriftState, string> = {
  in_sync: 'bg-received-wash text-received',
  price: 'bg-st-unpaid/10 text-st-unpaid',
  discount_base: 'bg-expected-wash text-gold-deep',
  minimum_for_promos: 'bg-expected-wash text-gold-deep',
  missing_on_ym: 'bg-st-unpaid/10 text-st-unpaid',
  never_collected: 'bg-bone text-ink-muted',
  forge_price_not_set: 'bg-st-unpaid/10 text-st-unpaid',
}

/** The Yandex Market price concepts, each with one source (docs/ymarket-pricing.md «Price concepts»). */
export const YM_PRICE_LABELS = {
  ourPrice: 'Наша цена на Маркете',
  marketPrice: 'Цена Маркета',
  promoPrice: 'Цена в акции',
  sellsFor: 'Продаётся за',
} as const

export const BESTSELLER_TIER_LABELS: Record<YmBestsellerTier, string> = {
  light: 'Лайт-бестселлер',
  bestseller: 'Бестселлер',
  super: 'Супербестселлер',
  top: 'Топ-бестселлер',
  none: 'Не бестселлер',
}

export const RECONCILE_LABELS: Record<YmReconcileDirection, string> = {
  forge_to_ym: 'Вернуть наши цены',
  ym_to_forge: 'Принять цены Маркета',
}

export const RECONCILE_EXPLANATIONS: Record<YmReconcileDirection, string> = {
  forge_to_ym: 'Цены Forge будут отправлены на Яндекс Маркет.',
  ym_to_forge: 'Цены с Маркета станут ценами Forge. На Маркет ничего не отправляется.',
}

const UNAVAILABLE_REASONS: Record<string, string> = {
  not_exposed_by_api: 'Маркет не отдаёт это значение через API',
  not_collected: 'Ещё не собрано',
  never_collected: 'Данные ещё не собирались',
  collect_failed: 'Сбор данных завершился ошибкой',
  offer_not_found: 'Предложение не найдено на Маркете',
  missing_on_ym: 'Предложения нет на Маркете',
  not_participating: 'Предложение не участвует в программе',
  no_competitors: 'Конкурентов с этим товаром нет',
  tariff_unavailable: 'Маркет не рассчитал тарифы',
  weight_not_measured: 'Вес изделия не измерен',
  not_eligible: 'Предложение не подходит под условия Маркета',
  rate_limited: 'Маркет ограничил частоту запросов',
}

/** Every absent value is explained in Russian; an unknown code still reads as a reason, never a blank. */
export function unavailableReasonText(code: string): string {
  return UNAVAILABLE_REASONS[code] ?? `Нет данных (${code})`
}

export const QUARANTINE_RISK_EXPLANATION =
  'Риск карантина: цена меняется слишком резко. Маркет может скрыть предложение с витрины, пока вы не подтвердите новую цену. '
  + 'Подтвердить можно будет после применения, если Маркет действительно отправит цену в карантин.'

export const MINIMUM_MODE_LABELS: Record<YmMinimumForPromosMode, string> = {
  keep: 'Не менять',
  price: 'Цена, ₽',
  percent_of_price: '% от цены',
  remove: 'Удалить',
}

export const FORGE_UNSET = 'не задано'
export const YM_ABSENT = 'нет на Маркете'

/** YM may omit the crossed-out price or the minimum for promos. */
export function formatYmMoney(value: number | null): string {
  return value === null ? YM_ABSENT : formatRubleAmount(value)
}

export type ClientEarnings = {
  pricePerGram: number | null
  payoutAfterMarketFee: number | null
  earnings: number | null
  earningsPercent: number | null
  unavailableReason: string | null
}

export type ClientEarningsInputs = {
  price: number
  weightGrams: number | null
  purchaseCost: number | null
  commission: number | null
  costUnavailableReason?: string | null
  tariffsUnavailableReason?: string | null
}

/** Mirrors calculate_ym_earnings; the commission is read from the API snapshot, never hard-coded. */
export function calculateClientEarningsFromInputs({
  price,
  weightGrams,
  purchaseCost,
  commission,
  costUnavailableReason,
  tariffsUnavailableReason,
}: ClientEarningsInputs): ClientEarnings {
  if (purchaseCost === null) return { pricePerGram: null, payoutAfterMarketFee: null, earnings: null, earningsPercent: null, unavailableReason: costUnavailableReason ?? 'cost_not_known' }
  if (weightGrams === null) return { pricePerGram: null, payoutAfterMarketFee: null, earnings: null, earningsPercent: null, unavailableReason: 'weight_not_measured' }
  if (commission === null) return { pricePerGram: null, payoutAfterMarketFee: null, earnings: null, earningsPercent: null, unavailableReason: tariffsUnavailableReason ?? 'tariffs_unavailable' }
  const payoutAfterMarketFee = Math.round((price - commission) * 100) / 100
  const earnings = Math.round((payoutAfterMarketFee - purchaseCost) * 100) / 100
  return {
    pricePerGram: Math.round((price / weightGrams) * 100) / 100,
    payoutAfterMarketFee,
    earnings,
    earningsPercent: Math.round((earnings / purchaseCost) * 10000) / 100,
    unavailableReason: null,
  }
}

export function calculateClientEarnings(offer: YmOfferPricing, price: number): ClientEarnings {
  return calculateClientEarningsFromInputs({
    price,
    weightGrams: offer.forge.weightGrams,
    purchaseCost: offer.forge.purchaseCost,
    commission: offer.costs?.commission ?? null,
    costUnavailableReason: offer.forge.purchaseCostUnavailableReason,
    tariffsUnavailableReason: offer.costsUnavailableReason,
  })
}

/** «4 786 ₽ с Пэй · 4 934 ₽ без Пэй», or one amount when both are the same. */
export function showcasePricesText(pay: number, noPay: number | null): string {
  return noPay === null || noPay === pay
    ? `${formatRubleAmount(pay)} с Пэй и без`
    : `${formatRubleAmount(pay)} с Пэй · ${formatRubleAmount(noPay)} без Пэй`
}

export function formatChangePercent(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—'
  const rounded = Math.round(value * 10) / 10
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : ''
  return `${sign}${Math.abs(rounded).toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`
}
